import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as SettledIcon,
  ArrowUpward as CollectIcon,
  ArrowDownward as PayOutIcon,
} from '@mui/icons-material';
import { useMyManagerDetail } from '../../hooks';
import { useDocumentTitle } from '../../hooks/core';
import { PlayerAvatar } from '../../components/shared/PlayerAvatar';
import type { BoardAccountingDetail, LedgerEntry } from '../../types';
import { formatEntryType, formatAccountingDate } from '../../utils/accounting';

export const MyManagerDetailPage = () => {
  const { managerId } = useParams<{ managerId: string }>();
  const navigate = useNavigate();
  const { detail, isLoading, error } = useMyManagerDetail(managerId || '');

  const managerName = detail?.manager?.displayName || 'Unknown';

  useDocumentTitle(detail ? `Balances - ${managerName}` : 'My Balances');

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!detail) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        Manager not found
      </Alert>
    );
  }

  // Check if ALL boards are settled
  const allBoardsSettled = detail.boards.every(board => {
    const amountDue = board.totalOwed - board.totalPaid;
    const isPaid = amountDue <= 0;
    const hasWinnings = board.totalEarned > 0;
    return isPaid && (!hasWinnings || board.winningsFullyPaid);
  });
  const hasUnsettledItems = !allBoardsSettled;

  // Calculate actionable amounts
  const amountYouOwe = detail.netBalance < 0 ? Math.abs(detail.netBalance) : 0;
  const amountOwedToYou = detail.netBalance > 0 ? detail.netBalance : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/balances')}
          variant="outlined"
        >
          Back
        </Button>
        <PlayerAvatar
          displayName={detail.manager?.displayName || ''}
          handle={detail.manager?.handle}
          iconName={detail.manager?.iconName}
          size="large"
          showBadge={false}
        />
        <Typography variant="h4">
          {managerName}
        </Typography>
        {!hasUnsettledItems && (
          <Chip label="Fully Settled" color="success" icon={<SettledIcon />} />
        )}
      </Box>

      {/* Primary Status Card */}
      {hasUnsettledItems ? (
        <Paper
          sx={{
            p: 3,
            mb: 3,
            bgcolor: amountYouOwe > 0 ? 'error.50' : 'success.50',
            border: 2,
            borderColor: amountYouOwe > 0 ? 'error.main' : 'success.main',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {amountYouOwe > 0 ? (
              <CollectIcon sx={{ fontSize: 40, color: 'error.main' }} />
            ) : (
              <PayOutIcon sx={{ fontSize: 40, color: 'success.main' }} />
            )}
            <Box>
              <Typography variant="overline" color="text.secondary">
                {amountYouOwe > 0 ? 'You Owe' : 'Owed to You'}
              </Typography>
              <Typography variant="h4" fontWeight="bold" color={amountYouOwe > 0 ? 'error.main' : 'success.main'}>
                ${amountYouOwe > 0 ? amountYouOwe.toFixed(2) : amountOwedToYou.toFixed(2)}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {amountYouOwe > 0
              ? `You owe ${managerName} this amount for your squares across ${detail.boards.length} board${detail.boards.length > 1 ? 's' : ''}.`
              : `${managerName} owes you this amount in winnings across ${detail.boards.length} board${detail.boards.length > 1 ? 's' : ''}.`
            }
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50', border: 2, borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SettledIcon sx={{ fontSize: 40, color: 'success.main' }} />
            <Box>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                All Settled Up
              </Typography>
              <Typography variant="body2" color="text.secondary">
                No outstanding balance with {managerName}.
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Historical Summary - Collapsible */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle1" fontWeight="bold">
            Historical Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="overline" color="text.secondary">Square Purchases</Typography>
              <Typography variant="h6">${detail.totalOwed.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total cost of all squares claimed
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Payments Made</Typography>
              <Typography variant="h6" color="success.main">${detail.totalPaid.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Money you've paid to this manager
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Winnings Earned</Typography>
              <Typography variant="h6" color="info.main">${detail.totalEarned.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Total prize money won
              </Typography>
            </Box>
            <Box>
              <Typography variant="overline" color="text.secondary">Winnings Received</Typography>
              <Typography variant="h6">${detail.winningsPaidOut.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">
                Prize money already received
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary">
            <strong>How the balance is calculated:</strong> Net Balance = Winnings Earned + Payments Made - Square Purchases.
            A negative balance means you owe money. A positive balance means you're owed winnings.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Per-Board Details */}
      <Typography variant="h6" gutterBottom>
        Boards ({detail.boards.length})
      </Typography>

      {detail.boards.map((board) => (
        <BoardCard key={board.board.id} board={board} />
      ))}
    </Box>
  );
};

// Board Card Component (read-only version)
interface BoardCardProps {
  board: BoardAccountingDetail;
}

const BoardCard = ({ board }: BoardCardProps) => {
  const amountDue = board.totalOwed - board.totalPaid;
  const isPaid = amountDue <= 0;
  const hasWinnings = board.totalEarned > 0;
  const isSettled = isPaid && (!hasWinnings || board.winningsFullyPaid);

  // Board result: what the player gained or lost by playing this board
  const boardResult = board.totalEarned - board.totalOwed;

  return (
    <Accordion
      sx={{
        mb: 1,
        bgcolor: isSettled ? '#e8f5e9' : '#fff',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
          <Typography fontWeight="bold" sx={{ minWidth: 200 }}>
            {board.board.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {board.board.homeTeam} vs {board.board.awayTeam}
          </Typography>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2">
              Result: <strong style={{ color: boardResult >= 0 ? '#2e7d32' : '#d32f2f' }}>
                {boardResult >= 0 ? '+' : '-'}${Math.abs(boardResult).toFixed(2)}
              </strong>
            </Typography>
            {isSettled && <SettledIcon sx={{ color: 'success.main' }} />}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {/* Financial Summary */}
        <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Squares</Typography>
            <Typography>{board.claimedSquares}/{board.approvedSquares}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Owed</Typography>
            <Typography>${board.totalOwed.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Paid</Typography>
            <Typography color="success.main">${board.totalPaid.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Winnings</Typography>
            <Typography color="info.main">${board.totalEarned.toFixed(2)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Winnings Received</Typography>
            <Typography>${board.winningsPaidOut.toFixed(2)}</Typography>
          </Box>
        </Box>

        {/* View Board Link */}
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="text"
            component={RouterLink}
            to={`/boards/${board.board.slug || board.board.id}`}
          >
            View Board
          </Button>
        </Box>

        {/* Ledger Entries */}
        {board.ledgerEntries.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Transaction History
            </Typography>
            <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1 }}>
              {board.ledgerEntries.map((entry: LedgerEntry) => (
                <Box
                  key={entry.id}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 0.5,
                    borderBottom: '1px solid #e0e0e0',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box>
                    <Typography variant="body2">
                      {formatEntryType(entry.entryType, 'player')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatAccountingDate(entry.createdAt)} - {entry.description}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={entry.amount >= 0 ? 'success.main' : 'error.main'}
                  >
                    {entry.amount >= 0 ? '+' : ''}${entry.amount.toFixed(2)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default MyManagerDetailPage;
